import { useEffect, useState, useCallback } from "react";
import { api, currency } from "../lib/api";
import { PageHeader } from "../components/Bits";
import { Card } from "../components/ui/card";
import { AlertTriangle, TrendingUp, ClipboardCheck, Factory, PackageX } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

const Stat = ({ label, value, icon: Icon, hint, tone = "slate" }) => (
  <Card className="p-5 border-slate-200 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`h-8 w-8 grid place-items-center rounded-md bg-${tone}-50 text-${tone}-600 border border-${tone}-100`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="font-display text-3xl font-extrabold text-slate-900">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get("/dashboard/stats").then((r) => setData(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-8 text-slate-500">Yükleniyor...</div>;

  return (
    <div className="p-8">
      <PageHeader eyebrow="Genel Bakış" title="Gösterge Paneli" subtitle="İşletmenizin özet performansı" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Stat label="Aktif Siparişler" value={data.active} icon={Factory} tone="blue" hint="Üretim akışında" />
        <Stat label="Toplam Gelir" value={currency(data.revenue)} icon={TrendingUp} tone="amber" hint={`${data.completed} sipariş tamamlandı`} />
        <Stat label="Net Kâr" value={currency(data.profit)} icon={ClipboardCheck} tone="emerald" hint={`Maliyet: ${currency(data.production_cost)}`} />
        <Stat label="Kritik Stok" value={data.low_stock_count} icon={AlertTriangle} tone="red" hint="Minimum eşiğin altında" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Son 14 gün</div>
              <h3 className="font-display text-lg font-bold text-slate-900">Sipariş & Gelir Akışı</h3>
            </div>
          </div>
          <div className="h-64 min-h-[240px]">
            <ResponsiveContainer>
              <LineChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Line yAxisId="l" type="monotone" dataKey="orders" stroke="#2563EB" strokeWidth={2} dot={false} name="Sipariş" />
                <Line yAxisId="r" type="monotone" dataKey="revenue" stroke="#16A34A" strokeWidth={2} dot={false} name="Gelir ($)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Aşama Dağılımı</div>
          <h3 className="font-display text-lg font-bold text-slate-900 mb-3">Aktif Aşamalar</h3>
          <div className="h-64 min-h-[240px]">
            <ResponsiveContainer>
              <BarChart data={data.per_stage.map((p) => ({ name: p.stage.name, count: p.count, fill: p.stage.color }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="mt-6 p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <PackageX className="h-4 w-4 text-red-600" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Uyarılar</div>
        </div>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Kritik Stok Malzemeleri</h3>
        {data.low_stock.length === 0 ? (
          <div className="text-sm text-slate-500">Şu an kritik stok bulunmuyor.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.low_stock.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50 px-4 py-3">
                <div>
                  <div className="font-semibold text-slate-900">{m.name}</div>
                  <div className="text-xs text-slate-500">Mevcut: {m.stock} {m.unit} · Min: {m.min_stock}</div>
                </div>
                <span className="text-xs font-bold text-red-700 uppercase tracking-widest">Düşük</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
